import { ActionIcon, Badge, Box, Divider, Flex, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { CohortType, UserStatus } from '@prisma/client';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { MyIcon } from '@web/common-ui';
import { useParticipantDataStore } from '../../../store';
import { InfoItem } from '../../dataCards';
import classes from '../ParticipantIdentification/ParticipantIdentification.module.css';
import { NachversandEligibilityModal } from './NachversandEligibilityModal';

export const NachversandEligibility = observer(() => {
  const participantDataStore = useParticipantDataStore();
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation('nachversand');

  const eligibilityBadge = (eligible: boolean | null) => {
    if (eligible === true) {
      return <Badge color="primary">{t('Options.Yes')}</Badge>;
    }
    if (eligible === false) {
      return <Badge color="red">{t('Options.No')}</Badge>;
    }
    return <Badge color="gray">{t('Options.NotYetDetermined')}</Badge>;
  };

  const participant = participantDataStore.data?.Participant;
  if (!participant) {
    return null;
  }
  // Only group B ships in part 2, so for groups A and C the badge would read as a shipping permission
  if (
    participant.cohortType === CohortType.ACCESS ||
    participant.cohortType === CohortType.CONTROL_GROUP
  ) {
    return null;
  }

  const isDeactivated =
    participantDataStore.data?.status === UserStatus.DEACTIVATED;
  // "Nein" is final (reactivation is developer-only), so there is nothing to edit
  const isEditable =
    !isDeactivated && participant.nachversandEligible !== false;

  return (
    <>
      <Divider color="#e2e0d6" my="6px" />
      <InfoItem
        // invisible spacer of the sibling rows' icon size, so the row aligns with
        // their indented text
        icon={<Box w="24px" />}
        customText={
          <Flex gap="8px" align="center">
            <Text className={classes.baseText}>{t('Label')}</Text>
            {eligibilityBadge(participant.nachversandEligible)}
            {isEditable && (
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={open}
                aria-label={t('EditTitle')}
                title={t('EditTitle')}
              >
                <MyIcon name="edit" style={{ fill: 'grey' }} />
              </ActionIcon>
            )}
          </Flex>
        }
      />
      <Divider color="#e2e0d6" my="6px" />
      <NachversandEligibilityModal
        opened={opened}
        onClose={close}
        currentValue={participant.nachversandEligible}
      />
    </>
  );
});
